<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { dynamicTime } from 'shared/helpers/timeHelper';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import ContactLabels from 'dashboard/components-next/Contacts/ContactLabels/ContactLabels.vue';

const props = defineProps({
  selectedContact: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();

const avatarFile = ref(null);
const avatarUrl = ref('');

const createdAt = computed(() => {
  return props.selectedContact?.createdAt
    ? dynamicTime(props.selectedContact.createdAt)
    : '';
});

const lastActivityAt = computed(() => {
  return props.selectedContact?.lastActivityAt
    ? dynamicTime(props.selectedContact.lastActivityAt)
    : '';
});

const avatarSrc = computed(() => {
  return avatarUrl.value ? avatarUrl.value : props.selectedContact?.thumbnail;
});

const companyName = computed(
  () => props.selectedContact?.additionalAttributes?.companyName || ''
);

const handleAvatarUpload = async ({ file, url }) => {
  avatarFile.value = file;
  avatarUrl.value = url;

  try {
    await store.dispatch('contacts/update', {
      id: props.selectedContact.id,
      avatar: file,
      isFormData: true,
    });
    useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.UPLOAD.SUCCESS_MESSAGE'));
  } catch {
    useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.UPLOAD.ERROR_MESSAGE'));
  }
};

const handleAvatarDelete = async () => {
  try {
    if (props.selectedContact?.id) {
      await store.dispatch('contacts/deleteAvatar', props.selectedContact.id);
      useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.DELETE.SUCCESS_MESSAGE'));
    }
    avatarFile.value = null;
    avatarUrl.value = '';
  } catch (error) {
    useAlert(
      error.message
        ? error.message
        : t('CONTACTS_LAYOUT.DETAILS.AVATAR.DELETE.ERROR_MESSAGE')
    );
  }
};
</script>

<template>
  <div
    class="flex flex-col items-start w-full gap-3 p-4 bg-n-solid-2 outline outline-1 outline-n-container -outline-offset-1 rounded-xl"
  >
    <Avatar
      :src="avatarSrc || ''"
      :name="selectedContact?.name || ''"
      :size="56"
      allow-upload
      @upload="handleAvatarUpload"
      @delete="handleAvatarDelete"
    />
    <div class="flex flex-col w-full gap-1">
      <h3 class="text-base font-medium truncate text-n-slate-12">
        {{ selectedContact?.name }}
      </h3>
      <span
        v-if="selectedContact?.identifier"
        class="inline-flex items-center gap-1 text-xs text-n-slate-11"
      >
        <span class="flex-shrink-0 i-ph-user-gear text-n-slate-10 size-3.5" />
        <span class="truncate">{{ selectedContact?.identifier }}</span>
      </span>
      <span class="inline-flex items-center gap-1 text-xs text-n-slate-11">
        {{ t('CONTACTS_LAYOUT.DETAILS.CREATED_AT', { date: createdAt }) }}
        <span aria-hidden="true">&middot;</span>
        {{
          t('CONTACTS_LAYOUT.DETAILS.LAST_ACTIVITY', {
            date: lastActivityAt,
          })
        }}
      </span>
    </div>
    <div
      v-if="
        selectedContact?.email || selectedContact?.phoneNumber || companyName
      "
      class="flex flex-col w-full gap-2"
    >
      <div
        v-if="selectedContact?.email"
        class="flex items-center min-w-0 gap-2 text-sm text-n-slate-11"
      >
        <span class="flex-shrink-0 i-lucide-mail size-4 text-n-slate-10" />
        <span class="truncate" :title="selectedContact.email">
          {{ selectedContact.email }}
        </span>
      </div>
      <div
        v-if="selectedContact?.phoneNumber"
        class="flex items-center min-w-0 gap-2 text-sm text-n-slate-11"
      >
        <span class="flex-shrink-0 i-lucide-phone size-4 text-n-slate-10" />
        <span class="truncate">{{ selectedContact.phoneNumber }}</span>
      </div>
      <div
        v-if="companyName"
        class="flex items-center min-w-0 gap-2 text-sm text-n-slate-11"
      >
        <span
          class="flex-shrink-0 i-lucide-building-2 size-4 text-n-slate-10"
        />
        <span class="truncate">{{ companyName }}</span>
      </div>
    </div>
    <ContactLabels :contact-id="selectedContact?.id" />
  </div>
</template>
